import { useEffect, useState } from "react";
import { setAppData } from "../App";
import DataTable from "../components/DataTable";
import Page from "../components/Page";
import TableActions from "../components/TableActions";
import useLocalStorage, { useSetLocalState } from "../lib/useLocalStorage";
import { useHistory } from "react-router-dom";
import { useUserProvider } from "../providers/UserProvider";
import { useOnboardingProvider } from "../providers/OnboardingProvider";
import copy from "../assets/images/copy.png";

import { GetApp, DisableApp, DisableScopedApp, GetAppResource } from "../services/api/app";
import { Tooltip } from "@material-ui/core";
import { nanoid } from 'nanoid';
import { useModalProvider } from "../providers/ModalProvider";
import { useLoaderProvider } from "../providers/LoaderProvider";

const BusinessList = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentApp, setCurrentApp] = useLocalStorage('currentApp', null);
  const { showModal, closeModal } = useModalProvider();
  const businessLinks = [
    {
      url: "pr/join",
      type: "PRO",
    },
    {
      url: "prpl/join",
      type: "PRO+",
    },
    {
      url: "fr/join",
      type: "FREE",
    },
  ];

  const [data, setData] = useState({
    loading: false,
    data: [],
    count: 0,
  });

  const history = useHistory();

  // const { user } = useUserProvider();
  const { user, setUser } = useUserProvider();
  const { setOnboardingData } = useOnboardingProvider();
  const setLiteUnlockCode = useSetLocalState('lite');
  const { doSetShowLoadingModal } = useLoaderProvider();

  const loadData = async () => {
    setData({ ...data, loading: true });
    GetAppResource(user, "children")
      .then((res) => res.json())
      .then((jsonData) => {
        setData({
          loading: false,
          data: jsonData.data,
          count: jsonData.data.length,
        });
      });
  };

  const handleGoToOnWizard = async (app) => {
    const selectedApp = {
      id: app.id,
      initials: app.app_name,
      name: app.app_name,
      plus: app.goplus,
      lite: app.goplus,
      biz_plus: app.biz_plus,
      isPending: app.is_pending,
    };
    setCurrentApp(selectedApp);
    if (selectedApp.id !== user.currentApp()) {
      doSetShowLoadingModal(true);
      const app = selectedApp;
      const currentApp = () => app.id;

      const _user = { ...user, app, currentApp };

      try {
        if (app.lite) {
          const res = await GetApp(_user, _user.idToken);
          const jsonRes = await res.json();
          const app_setup = jsonRes.data.setup;
          if (app_setup?.premium_lite && app_setup?.lite_unlock_code) {
            setLiteUnlockCode(app_setup.lite_unlock_code);
          }
        } else setLiteUnlockCode('');
      } catch {
        setLiteUnlockCode('');
      }

      setUser({ ...user, app, currentApp });

      setAppData({
        idToken: user.idToken,
        data: user.data,
        isSuper: user.isSuper,
        currentApp,
        app,
        setUser,
        postCallback: () => {
          doSetShowLoadingModal(false);
          if(user?.isSuper) {
            history.push('/onboarding');
          }
        },
        setOnboardingData
      });
    }
  };

  const links = [
    { name: "Home", url: "/" },
    { name: "Businesses", url: "/business" },
  ];

  const metadata = [
    {
      name: "key",
      label: "Key",
      parser: (data) => nanoid(),
    },
    {
      name: "name",
      label: "Name of sub-app",
      parser: (data) => data.name,
      ellipsis: true,
    },
    {
      name: "app_type",
      label: "App Type",
      parser: (data) => data.app_type,
      ellipsis: true,
    },
    {
      name: "active",
      label: "Active?",
      parser: (data) => (data.active ? "Yes" : "No"),
      ellipsis: true,
    },

    {
      name: "actions",
      label: "Actions",
      parser: (data) => (
        <TableActions
          onUpdateClick={updateButtonHandler}
          item_id={data.id}
          onDeleteClick={() => {
            showModal(
              <div>
                <div>Are you sure you to delete this Business?</div>
                <div className="flex flex-row items-center gap-3 mx-auto justify-center">
                  <button
                    className="ttnk-button mt-2"
                    onClick={() => {
                      const disableAction = user?.isSuper
                        ? () => DisableApp(data.id, user.idToken)
                        : () => DisableScopedApp(user.currentApp(), data.id, user.idToken);

                      disableAction().then(() => {
                        closeModal();
                        loadData();
                      });
                    }}
                  >
                    Yes
                  </button>
                  <button className="ttnk-button mt-2" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </div>
            );
          }}
        />
      ),
    },
    {
      name: "onboarder",
      label: "",
      parser: (data) => (
        data.onboarding.wizard ? (
          <button
            onClick={() => handleGoToOnWizard(data)}
            className="h-8 border border-black rounded-full items-center justify-center flex py-1 px-4"
          >
            Go to Wizard
          </button>
        ) : null
      ),
    },
  ];

  const AddButton = () => {
    return (
      <button
        className="ttnk-button add-button"
        onClick={() => history.push("/business/new")}
      >
        Add Business
      </button>
    );
  };

  const updateButtonHandler = (id) => {
    history.push(`/business/${id}`);
  };

  useEffect(() => {
    if (
      user?.app_data?.setup?.enterprise ||
      user?.app_data?.reseller_of?.length > 0
    )
      return;

    history.push("/");
  }, [user?.app_data?.setup?.enterprise, user?.app_data?.reseller_of?.length]);

  const currentBusinessLinks = user?.app_data?.reseller_of?.length > 0 &&
    !user?.app_data?.setup?.enterprise ? [] : businessLinks;

  return (
    <Page
      links={links}
      title="Business List"
      // titleComponent={AddButton()}
      extra={
        <div className="mb-5">
          {currentBusinessLinks.map((link, index) => {
              return (
                <div
                  key={index}
                  className="flex flex-row items-center text-gray-700 font-medium"
                >
                  Your {link.type} Account Setup WEB URL:{" "}
                  <span
                    className="underline ml-1 cursor-pointer"
                    onClick={() => {
                      window.open(
                        `${window.location.origin}/${user?.app?.id}/${link.url}`,
                        "_blank"
                      );
                    }}
                  >
                    {window.location.origin}/{user?.app?.id}/{link.url}
                  </span>

                  <Tooltip title={<div>Copy</div>}>
                    <img
                      src={copy}
                      className="h-5 ml-2 w-5 cursor-pointer hover:scale-125"
                      alt="copy"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `${window.location.origin}/${user?.app?.id}/${link.url}`
                        )
                      }
                    />
                  </Tooltip>
                </div>
              );
          })}
        </div>
      }
    >
      <DataTable
        metadata={metadata}
        dataLoader={loadData}
        data={data}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        searchKeys={["name"]}
      />
    </Page>
  );
};

export default BusinessList;
